/**
 * Drawing pipeline — one honest strip instead of the old five KPI cards: every drawing in every one
 * of the 12 statuses, as a stacked bar in workflow order, with a clickable legend that filters the
 * All Drawings table.
 */
import { Pause } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';

const SEGMENT_FILL = {
  navy: 'var(--status-navy)',
  teal: 'var(--status-teal)',
  amber: 'var(--status-amber)',
  blue: 'var(--status-blue)',
  gray: 'var(--status-gray)',
  red: 'var(--status-red)',
  outline: 'var(--line)',
};

const PATTERN_FILL = {
  hold: 'repeating-linear-gradient(135deg, var(--status-amber) 0 3px, var(--status-amber-bg) 3px 6px)',
  rejected: 'repeating-linear-gradient(135deg, var(--status-red) 0 3px, var(--status-red-bg) 3px 6px)',
};

const fillOf = (group) => PATTERN_FILL[group.pattern] || SEGMENT_FILL[group.tone] || SEGMENT_FILL.gray;
const fmt = (value) => formatNumber(value, { maxDecimals: 0 });

export function PipelineStrip({ pipeline, activeKey, onSelect, loading = false }) {
  if (loading) {
    return (
      <section className="rounded-card border border-line bg-surface px-5 py-4 shadow-card" aria-busy="true" aria-label="Drawing pipeline">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Drawing pipeline</span>
          <span className="ui-skeleton-shimmer block h-5 w-56 rounded-full" />
        </div>
        <span className="ui-skeleton-shimmer mt-3 block h-2.5 w-full rounded-full" />
        <span className="ui-skeleton-shimmer mt-3 block h-3 w-3/4 rounded-full" />
      </section>
    );
  }

  const { total, projects, groups, approvedCount, approvedPct } = pipeline;
  const visible = groups.filter((group) => group.count > 0);

  return (
    <section className="rounded-card border border-line bg-surface px-5 py-4 shadow-card" aria-label="Drawing pipeline" data-testid="shop-drawings-pipeline">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Drawing pipeline</h2>
          <span className="text-[13px] text-ink-2">
            {fmt(total)} {total === 1 ? 'drawing' : 'drawings'} in {fmt(projects)} {projects === 1 ? 'project' : 'projects'}
          </span>
        </div>
        <div className="whitespace-nowrap" data-testid="shop-drawings-approved-pct">
          <span className="font-heading text-[22px] font-black leading-none tracking-[-0.02em] text-brand">{approvedPct}%</span>
          <span className="text-[12.5px] text-ink-3"> approved or completed ({fmt(approvedCount)} of {fmt(total)})</span>
        </div>
      </div>

      <div
        className="mt-2.5 flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-[color:var(--line-2)]"
        role="img"
        aria-label={visible.map((group) => `${group.label} ${group.count}`).join(', ') || 'No drawings yet'}
      >
        {visible.map((group, index) => (
          <span
            key={group.key}
            className={cn('block h-full', index === 0 && 'rounded-l-full', index === visible.length - 1 && 'rounded-r-full', activeKey && activeKey !== group.key && 'opacity-35')}
            style={{ flexGrow: group.count, flexBasis: 0, minWidth: 3, background: fillOf(group) }}
            title={`${group.label}: ${group.count}`}
          />
        ))}
      </div>

      <div className="mt-2.5 flex flex-wrap gap-x-3.5 gap-y-1.5" role="group" aria-label="Filter drawings by pipeline stage">
        {groups.map((group) => {
          const active = activeKey === group.key;
          return (
            <button
              key={group.key}
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(active ? null : group.key)}
              disabled={!group.count && !active}
              data-testid={`pipeline-${group.key}`}
              title={group.short ? group.label : undefined}
              className={cn(
                '-mx-1.5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent',
                active && 'bg-brand-soft text-ink ring-1 ring-[color:var(--brand-soft-border)]'
              )}
            >
              {group.pattern === 'hold' ? (
                <Pause className="h-3 w-3 text-status-amber" strokeWidth={3} aria-hidden="true" />
              ) : (
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: fillOf(group) }} aria-hidden="true" />
              )}
              {group.short || group.label}
              <b className="font-extrabold tabular-nums text-ink">{fmt(group.count)}</b>
            </button>
          );
        })}
      </div>
    </section>
  );
}
